import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';
import {
  Goal,
  GoalDetail,
  GoalComment,
  CreateGoalRequest,
  UpdateGoalRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  ActiveGoalsStats,
  ModelType,
} from 'src/features/Goals/types';

// --- Query Keys ---

const goalsQueryKey = (customer: string) =>
  ['useGoalsQuery', customer] as const;

const goalDetailQueryKey = (customer: string, goalId: string) =>
  ['useGoalDetailQuery', customer, goalId] as const;

const goalsStatsQueryKey = (customer: string, modelType: ModelType) =>
  ['useGoalsStatsQuery', customer, modelType] as const;

const goalCommentsQueryKey = (customer: string, goalId: string) =>
  ['useGoalCommentsQuery', customer, goalId] as const;

// --- Queries ---

export const useGoalsQuery = (
  customer: string,
  filters?: { modelType?: ModelType; modelParametersId?: string },
) => {
  const queryId = useMemo(() => goalsQueryKey(customer), [customer]);
  return useQuery(
    queryId,
    async () => {
      const params: Record<string, string> = { customer };
      if (filters?.modelType) params.model_type = filters.modelType;
      if (filters?.modelParametersId)
        params.model_parameters_id = filters.modelParametersId;

      const response = await request.get<{ data: Goal[] }>('/data/goals', {
        params,
      });
      return response.data.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

export const useGoalDetailQuery = (customer: string, goalId: string | null) => {
  const queryId = useMemo(
    () => goalDetailQueryKey(customer, goalId ?? ''),
    [customer, goalId],
  );
  return useQuery(
    queryId,
    async () => {
      if (!goalId) return null;
      const response = await request.get<{ data: GoalDetail }>(
        '/data/goals/goal',
        { params: { customer, goal_id: goalId } },
      );
      return response.data.data ?? null;
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer) && Boolean(goalId),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

export const useGoalsStatsQuery = (customer: string, modelType: ModelType) => {
  const queryId = useMemo(
    () => goalsStatsQueryKey(customer, modelType),
    [customer, modelType],
  );
  return useQuery(
    queryId,
    async () => {
      const response = await request.get<{ data: ActiveGoalsStats }>(
        '/data/goals/stats',
        { params: { customer, model_type: modelType } },
      );
      return response.data.data ?? null;
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

// --- Mutations ---

export const useCreateGoalMutation = (customer: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: CreateGoalRequest) => {
      const response = await request.post('/data/goals', data, {
        params: { customer },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalsQueryKey(customer));
        queryClient.invalidateQueries(['useGoalsStatsQuery', customer]);
      },
    },
  );
};

export const useUpdateGoalMutation = (customer: string, goalId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: UpdateGoalRequest) => {
      const response = await request.patch('/data/goals/goal', data, {
        params: { customer, goal_id: goalId },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalsQueryKey(customer));
        queryClient.invalidateQueries(goalDetailQueryKey(customer, goalId));
        queryClient.invalidateQueries(['useGoalsStatsQuery', customer]);
      },
    },
  );
};

export const useDeleteGoalMutation = (customer: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (goalId: string) => {
      const response = await request.delete('/data/goals/goal', {
        params: { customer, goal_id: goalId },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalsQueryKey(customer));
        queryClient.invalidateQueries(['useGoalsStatsQuery', customer]);
      },
    },
  );
};

// --- Comments Queries ---

export const useGoalCommentsQuery = (
  customer: string,
  goalId: string | null,
) => {
  const queryId = useMemo(
    () => goalCommentsQueryKey(customer, goalId ?? ''),
    [customer, goalId],
  );
  return useQuery(
    queryId,
    async () => {
      if (!goalId) return [];
      const response = await request.get<{ data: GoalComment[] }>(
        '/data/goals/comments',
        { params: { customer, goal_id: goalId } },
      );
      return response.data.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      enabled: Boolean(customer) && Boolean(goalId),
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
};

// --- Comments Mutations ---

export const useCreateCommentMutation = (customer: string, goalId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (data: CreateCommentRequest) => {
      const response = await request.post<{ data: GoalComment }>(
        '/data/goals/comments',
        data,
        { params: { customer, goal_id: goalId } },
      );
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalCommentsQueryKey(customer, goalId));
        queryClient.invalidateQueries(goalDetailQueryKey(customer, goalId));
        queryClient.invalidateQueries(goalsQueryKey(customer));
      },
    },
  );
};

export const useUpdateCommentMutation = (customer: string, goalId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({
      commentId,
      data,
    }: {
      commentId: string;
      data: UpdateCommentRequest;
    }) => {
      const response = await request.patch<{ data: GoalComment }>(
        '/data/goals/comments/comment',
        data,
        { params: { customer, comment_id: commentId } },
      );
      return response.data.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalCommentsQueryKey(customer, goalId));
        queryClient.invalidateQueries(goalDetailQueryKey(customer, goalId));
      },
    },
  );
};

export const useDeleteCommentMutation = (customer: string, goalId: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (commentId: string) => {
      const response = await request.delete('/data/goals/comments/comment', {
        params: { customer, comment_id: commentId },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(goalCommentsQueryKey(customer, goalId));
        queryClient.invalidateQueries(goalDetailQueryKey(customer, goalId));
        queryClient.invalidateQueries(goalsQueryKey(customer));
      },
    },
  );
};
